import { useEffect, useState } from "react";
import { ChevronUp, Loader2, Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { listFeatures, voteFeature, createFeature, updateFeature, deleteFeature } from "../lib/api";
import { useAuth } from "../lib/auth";

const STATUS_META = {
  planned:     { label: "Planned",     tone: "border-white/15 text-white" },
  in_progress: { label: "In Progress", tone: "border-[#FF3B30]/50 text-[#FF3B30]" },
  shipped:     { label: "Shipped",     tone: "border-emerald-500/50 text-emerald-300" },
};

export default function Roadmap() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [voting, setVoting] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const refresh = async () => {
    try {
      const data = await listFeatures();
      setItems(data.items || []);
      setIsAdmin(!!data.is_admin);
    } catch {
      toast.error("Couldn't load the roadmap");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [user?.user_id]);

  const handleVote = async (id) => {
    if (!user) {
      toast.error("Sign in to upvote features");
      return;
    }
    setVoting(id);
    try {
      const res = await voteFeature(id);
      setItems((prev) => prev.map((f) => f.id === id ? { ...f, voted: res.voted, votes: res.votes } : f));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vote failed");
    } finally {
      setVoting(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      const created = await createFeature({ title: newTitle, description: newDesc, status: "planned" });
      setItems((prev) => [created, ...prev]);
      setNewTitle(""); setNewDesc("");
      toast.success("Feature added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const updated = await updateFeature(id, { status });
      setItems((prev) => prev.map((f) => f.id === id ? updated : f));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this feature request?")) return;
    try {
      await deleteFeature(id);
      setItems((prev) => prev.filter((f) => f.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" /></div>;

  const grouped = { in_progress: [], planned: [], shipped: [] };
  items.forEach((f) => { (grouped[f.status] || grouped.planned).push(f); });

  return (
    <div className="mx-auto max-w-[900px] px-6 py-14 md:px-10" data-testid="roadmap-page">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 12</p>
      <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-7xl">
        Roadmap
      </h1>
      <p className="mt-4 max-w-xl text-sm text-neutral-400">
        What&apos;s coming, what we&apos;re building, and what&apos;s already live. Upvote the ideas you want most — the most-loved ones surface to the top of the queue.
      </p>
      {!user && (
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          Sign in with Google to upvote.
        </p>
      )}

      {isAdmin && (
        <form
          data-testid="admin-new-feature-form"
          onSubmit={handleCreate}
          className="mt-8 border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-4"
        >
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">Admin · Add Feature</p>
          <div className="mt-3 flex flex-col gap-2 md:flex-row">
            <input
              data-testid="admin-feature-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Feature title"
              className="flex-1 border border-white/15 bg-black px-3 py-2 text-white placeholder:text-neutral-600 focus:outline-none focus:border-white/40"
            />
            <button
              data-testid="admin-feature-add-btn"
              type="submit"
              className="flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
            >
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
          <input
            data-testid="admin-feature-desc"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Short description (optional)"
            className="mt-2 w-full border border-white/15 bg-black px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-white/40"
          />
        </form>
      )}

      <div className="mt-10 space-y-10">
        {[["in_progress", "In Progress"], ["planned", "Planned"], ["shipped", "Shipped"]].map(([key, label]) => (
          <section key={key} data-testid={`roadmap-section-${key}`}>
            <h2 className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              {label} · {grouped[key].length}
            </h2>
            {grouped[key].length === 0 ? (
              <p className="mt-3 border border-dashed border-white/10 p-4 font-mono text-[10px] uppercase tracking-widest text-neutral-600">
                Nothing here yet.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {grouped[key].map((f) => (
                  <FeatureRow
                    key={f.id}
                    feature={f}
                    voting={voting === f.id}
                    onVote={() => handleVote(f.id)}
                    isAdmin={isAdmin}
                    onStatusChange={(s) => handleStatusChange(f.id, s)}
                    onDelete={() => handleDelete(f.id)}
                  />
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

function FeatureRow({ feature, voting, onVote, isAdmin, onStatusChange, onDelete }) {
  const meta = STATUS_META[feature.status] || STATUS_META.planned;
  return (
    <li
      data-testid={`feature-row-${feature.id}`}
      className="flex items-start gap-3 border border-white/10 bg-neutral-950 p-4"
    >
      <button
        data-testid={`feature-vote-${feature.id}`}
        onClick={onVote}
        disabled={voting || feature.status === "shipped"}
        className={`flex flex-col items-center justify-center gap-0.5 border px-3 py-2 font-mono transition-colors disabled:opacity-40 ${
          feature.voted
            ? "border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]"
            : "border-white/20 bg-transparent text-white hover:border-[#FF3B30] hover:text-[#FF3B30]"
        }`}
        aria-label={feature.voted ? "Remove upvote" : "Upvote"}
      >
        {voting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronUp className="h-4 w-4" />}
        <span data-testid={`feature-votes-${feature.id}`} className="text-xs">{feature.votes}</span>
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-white">{feature.title}</h3>
          <span className={`border px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest ${meta.tone}`}>
            {meta.label}
          </span>
        </div>
        {feature.description && (
          <p className="mt-1 text-sm text-neutral-400">{feature.description}</p>
        )}
        {isAdmin && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select
              data-testid={`feature-status-select-${feature.id}`}
              value={feature.status}
              onChange={(e) => onStatusChange(e.target.value)}
              className="border border-white/15 bg-black px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white"
            >
              <option value="planned">Planned</option>
              <option value="in_progress">In Progress</option>
              <option value="shipped">Shipped</option>
            </select>
            <button
              data-testid={`feature-delete-${feature.id}`}
              onClick={onDelete}
              className="flex items-center gap-1 border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-neutral-400 hover:border-red-500/60 hover:text-red-400"
            >
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
